import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Icon from "../../components/Icon";

const SUPPORT_EMAIL = "support@choremouse.app";
const APP_NAME = "ChoreMouse";
const UPDATED = "October 2026";

function Section({ title, children }) {
  return (
    <section className="mb-7">
      <h2 className="font-heading font-extrabold text-xl text-ink mb-2">{title}</h2>
      <div className="space-y-2 text-ink-soft font-medium leading-relaxed">{children}</div>
    </section>
  );
}

const PAGES = {
  privacy: {
    title: "Privacy Policy",
    icon: "lock",
    body: (
      <>
        <p className="text-ink-soft font-medium mb-6">Last updated {UPDATED}. This policy explains what {APP_NAME} collects and how we use it.</p>
        <Section title="Who this app is for">
          <p>{APP_NAME} is designed for <b>parents and guardians</b>. Parents create the account. Children use a shared “kids door” inside the family account and never register, provide an email, or enter personal contact information.</p>
        </Section>
        <Section title="What we collect">
          <p>• <b>Parent account:</b> your name, email address, and a securely hashed password.</p>
          <p>• <b>Family data you create:</b> a family name, a kids access code, children's first names (or nicknames) and optional avatars/PINs, chores, points, rewards, redemption history, and photos you choose to upload for chores or rewards.</p>
          <p>• <b>Subscription data:</b> your plan, billing-provider identifiers, and payment status. Website checkout is processed by Stripe; mobile purchases are processed by Apple or Google and verified through RevenueCat. We do not store your full payment-card details.</p>
          <p>• <b>Basic technical data</b> needed to run the service (e.g. session tokens).</p>
          <p>We do <b>not</b> collect precise location, contacts, or advertising identifiers, and we do not sell data.</p>
        </Section>
        <Section title="Children's privacy (COPPA / GDPR-K)">
          <p>Accounts are created and controlled by a parent or guardian, who provides any information about their children. Children do not create accounts. Parents can view, edit, and delete their children's data at any time from <b>Family</b> and <b>Settings</b>. We collect the minimum needed to run a family chore chart and never ask children for personal contact details.</p>
        </Section>
        <Section title="How we use data">
          <p>Only to provide the app: signing in, showing chores and points, running your reward shop, and keeping your family's data saved.</p>
        </Section>
        <Section title="Your choices & deletion">
          <p>You can delete your entire account and all associated family data at any time from <b>Settings → Delete account</b>, or by emailing <a className="text-primary font-bold" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. Deletion is permanent.</p>
        </Section>
        <Section title="Contact">
          <p>Questions? Email <a className="text-primary font-bold" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p>
        </Section>
      </>
    ),
  },
  terms: {
    title: "Terms of Service",
    icon: "file-text",
    body: (
      <>
        <p className="text-ink-soft font-medium mb-6">Last updated {UPDATED}. By using {APP_NAME} you agree to these terms.</p>
        <Section title="The service"><p>{APP_NAME} helps families track chores and rewards. You are responsible for the accuracy of the information you add and for supervising your children's use.</p></Section>
        <Section title="Accounts"><p>You must be 18+ to create a parent account. Keep your password secure. You are responsible for activity under your account.</p></Section>
        <Section title="Acceptable use"><p>Don't misuse the service, attempt to break it, or use it to store unlawful content.</p></Section>
        <Section title="Rewards & points"><p>Points and rewards are a fun, in-family system with no real-world monetary value provided by us. Any allowance or treats are arranged and fulfilled entirely by your family.</p></Section>
        <Section title="Subscriptions & billing">
          <p>Your seven-day free trial begins when you create your family account. No payment card is required for this trial. When it ends, family and kid access pauses until the family owner enrolls in ChoreMouse Plus. Your saved family progress remains available when access resumes. Opening checkout does not start a second trial.</p>
          <p>Website subscriptions use Stripe. Manage or cancel them from <b>Settings → Manage subscription</b>. Purchases in the iPhone or Android app use the Apple App Store or Google Play; manage or cancel those subscriptions in the store account used for the purchase.</p>
          <p>The price, currency, taxes, and renewal terms are shown before you confirm a purchase. Subscriptions renew automatically until canceled. Turning off renewal preserves access through the end of the paid period. Settings and account deletion remain available while family access is paused.</p>
        </Section>
        <Section title="Disclaimer"><p>The service is provided “as is” without warranties. To the extent permitted by law, we are not liable for indirect or incidental damages.</p></Section>
        <Section title="Contact"><p>Email <a className="text-primary font-bold" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p></Section>
      </>
    ),
  },
  support: {
    title: "Support",
    icon: "life-buoy",
    body: (
      <>
        <p className="text-ink-soft font-medium mb-6">We're here to help you get the most out of {APP_NAME}.</p>
        <Section title="Contact us"><p>Email <a className="text-primary font-bold" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll reply within 2 business days.</p></Section>
        <Section title="Quick answers">
          <p><b>How do kids sign in?</b> Open the Kids door and enter the family code from your <b>Family</b> page. Kids pick their name and (if set) type their PIN.</p>
          <p><b>How are points calculated?</b> Kids earn points when you approve a completed chore, and spend them when you grant a reward. Balance = earned − spent.</p>
          <p><b>I forgot my password.</b> Use “Forgot password?” on the sign-in screen.</p>
          <p><b>How do I delete my data?</b> Go to <b>Settings → Delete account</b>.</p>
        </Section>
      </>
    ),
  },
  "delete-account": {
    title: "Delete your account",
    icon: "triangle-alert",
    body: (
      <>
        <p className="text-ink-soft font-medium mb-6">You can permanently delete your {APP_NAME} account and all associated data at any time.</p>
        <Section title="From inside the app">
          <p>1. Sign in as a parent.</p>
          <p>2. Tap the <b>gear icon</b> → <b>Settings</b>.</p>
          <p>3. Scroll to <b>Delete account</b> and confirm.</p>
          <p>This immediately and permanently removes your parent account, all children, chores, rewards, and history. It cannot be undone.</p>
          <p>Website subscriptions are canceled as part of deletion. If you subscribed through the Apple App Store or Google Play, cancel that subscription in your store account separately.</p>
        </Section>
        <Section title="By email">
          <p>Prefer we do it for you? Email <a className="text-primary font-bold" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> from your account's email address and we'll delete everything within 30 days.</p>
        </Section>
      </>
    ),
  },
  parents: {
    title: "For Parents",
    icon: "shield-check",
    body: (
      <>
        <p className="text-ink-soft font-medium mb-6">{APP_NAME} was built to be safe, simple, and fully in your control.</p>
        <Section title="You're in control"><p>Only you create the account. You add each child, decide their chores, points, PINs and rewards, and approve everything before points are awarded or rewards are granted.</p></Section>
        <Section title="No ads, no strangers"><p>There are no advertisements, no public profiles, no chat, and no way for anyone outside your family to find or contact your children. Kids join only with your private family code.</p></Section>
        <Section title="Minimal data"><p>Children never provide an email or contact details. First names or nicknames are enough. You can edit or delete any child's data instantly.</p></Section>
        <Section title="Full transparency"><p>Read our <Link to="/privacy" className="text-primary font-bold">Privacy Policy</Link> and <Link to="/terms" className="text-primary font-bold">Terms</Link>. Questions? <a className="text-primary font-bold" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p></Section>
      </>
    ),
  },
};

export default function Legal({ page }) {
  const navigate = useNavigate();
  const data = PAGES[page] || PAGES.support;
  return (
    <div className="min-h-screen bg-canvas" data-testid={`legal-${page}`}>
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-5 h-16 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-slate-100 grid place-items-center" data-testid="legal-back" aria-label="Back"><Icon name="arrow-left" className="text-ink-soft" width={20} height={20} /></button>
          <div className="flex items-center gap-2"><Icon name={data.icon} className="text-primary" width={20} height={20} /><span className="font-heading font-black text-lg text-ink">{data.title}</span></div>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 py-8">
        <h1 className="font-heading font-black text-3xl text-ink mb-5">{data.title}</h1>
        {data.body}
        <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap gap-x-5 gap-y-2 text-sm font-bold text-ink-soft">
          <Link to="/privacy" className="hover:text-primary">Privacy</Link>
          <Link to="/terms" className="hover:text-primary">Terms</Link>
          <Link to="/parents" className="hover:text-primary">For Parents</Link>
          <Link to="/support" className="hover:text-primary">Support</Link>
          <Link to="/delete-account" className="hover:text-primary">Delete account</Link>
        </div>
      </main>
    </div>
  );
}
